/**
 * Service layer — QC Versions (markup / review chain)
 *
 * Wraps /api/qc-versions. A version is one markup-and-review round; parent_version_id
 * links rounds into an audit chain. Every save is a new row — nothing is overwritten.
 */

async function call(method, body, qs) {
  const token = await window.__clerkGetToken()
  const url = '/api/qc-versions' + (qs ? '?' + qs : '')
  const res = await fetch(url, {
    method,
    headers: method === 'GET'
      ? { 'Authorization': 'Bearer ' + token }
      : { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: method === 'GET' ? undefined : JSON.stringify(body),
  })
  if (!res.ok) {
    const b = await res.json().catch(() => ({}))
    throw new Error(b.error || 'QC version request failed: ' + res.status)
  }
  return res.json()
}

/** Get or create the current open (non-approved) version for a sheet. Returns { version, reused }. */
export function createOrGetVersion({ drawingSheetId, sheetRevisionId, basePdfUrl, parentVersionId } = {}) {
  return call('POST', { action: 'create', drawingSheetId, sheetRevisionId, basePdfUrl, parentVersionId })
}

/**
 * Look for an existing version WITHOUT starting one — merely opening the QC tool to view a
 * drawing must not enroll it in the modern workflow, since an unapproved round now blocks that
 * sheet's milestone from being posted (see PostMilestoneModal.jsx's QC gate). Returns the same
 * shape as createOrGetVersion when a round already exists (open or approved); when none exists
 * yet, `version` is null and no row is created — call createOrGetVersion explicitly (e.g. from a
 * "Start QC Round" action) to actually begin one.
 */
export function peekVersion({ drawingSheetId, sheetRevisionId, basePdfUrl } = {}) {
  return call('POST', { action: 'create', drawingSheetId, sheetRevisionId, basePdfUrl, peek: true })
}


/** Load one version with its findings + runs. */
export function getVersion(versionId) {
  return call('GET', null, 'versionId=' + encodeURIComponent(versionId))
}

/** Persist the annotation layer (vector JSON in PDF coordinates) for a version. */
export function saveAnnotations(versionId, annotationJson) {
  return call('POST', { action: 'saveAnnotations', versionId, annotationJson })
}

/**
 * Record the designer's revised PDF URL on a version. Returns { ok, reanchored } —
 * `reanchored` is true when the PDF's coordinate frame differed from the round's and
 * the server reprojected every anchor/shape onto it (caller should reload the round).
 * Rejects approved (frozen) rounds and PDFs whose visual aspect doesn't match.
 */
export function setRevisionPdf(versionId, revisionPdfUrl) {
  return call('POST', { action: 'setRevisionPdf', versionId, revisionPdfUrl })
}

/** Set version status: 'in_review' | 'resolved' | 'approved'. */
export function setVersionStatus(versionId, status) {
  return call('POST', { action: 'setStatus', versionId, status })
}

/** Approve (finalize) a round — the approved drawing becomes the sheet's official PDF. */
export function approveVersion(versionId) {
  return call('POST', { action: 'approve', versionId })
}

/**
 * Manager-only: mark a revision "QC approved" without running it through QC — for
 * legacy/imported drawings that predate the QC tool. Server-side rejects unless the
 * caller's seat is manager/admin, and unless the revision has zero existing qc_versions
 * rows (a real or started QC chain always goes through approveVersion instead).
 * @param {string} drawingSheetId
 * @param {string} sheetRevisionId
 * @param {string} [reason]
 */
export function overrideApproveRevision(drawingSheetId, sheetRevisionId, reason) {
  return call('POST', { action: 'overrideApprove', drawingSheetId, sheetRevisionId, reason })
}

/**
 * Admin-only: swap the PDF of an already-approved revision (e.g. a titleblock correction)
 * without reopening QC. The current approved round is closed ('superseded', its findings and
 * annotations preserved for history) and a new round is created already approved, pointing at
 * the new PDF. Server-side rejects unless the caller's seat is admin, unless the revision has
 * an approved round to replace, and unless the new PDF's visual aspect matches the one it's
 * replacing (a different shape is a different sheet, not a correction).
 * @param {string} drawingSheetId
 * @param {string} sheetRevisionId
 * @param {string} newPdfUrl — already uploaded to a stable URL (see uploadRevisionPdf in services/sheets.js)
 * @param {string} [reason]
 */
export function overrideReplacePdf(drawingSheetId, sheetRevisionId, newPdfUrl, reason) {
  return call('POST', { action: 'overrideReplacePdf', drawingSheetId, sheetRevisionId, newPdfUrl, reason })
}

/** QC checker finished commenting — lock new markup for this round (drafter then addresses). */
export function completeReview(versionId) {
  return call('POST', { action: 'completeReview', versionId })
}

/** Send the sheet back for another revision — opens the next round, carrying unresolved items. */
export function sendBackForRevision(versionId) {
  return call('POST', { action: 'sendBack', versionId })
}

/**
 * Upload a PDF (revised or flattened) to the qc-pdfs Storage bucket for a version.
 * @returns {Promise<string>} public URL
 */
export async function uploadQcPdf(versionId, file, kind = 'revised') {
  const { signedUrl, publicUrl } = await call('POST', { action: 'getUploadUrl', versionId, filename: file.name, kind })
  const put = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/pdf', 'x-upsert': 'true' }, body: file })
  if (!put.ok) throw new Error('Upload failed: ' + put.status)
  return publicUrl
}

/**
 * Upload + record the designer's revised PDF on a version.
 * Returns { url, reanchored } — see setRevisionPdf.
 */
export async function uploadRevisionPdf(versionId, file) {
  const url = await uploadQcPdf(versionId, file, 'revised')
  const r = await setRevisionPdf(versionId, url)
  return { url, reanchored: !!r?.reanchored }
}

/**
 * Copy the sheet's current revision PDF into qc-pdfs and set it as the revised PDF.
 * Returns { url, reanchored } — see setRevisionPdf. LEGACY — new flow uses startNextRound.
 */
export function pullLatestRevisedPdf(versionId, sourceUrl) {
  return call('POST', { action: 'pullLatestRevised', versionId, sourceUrl })
    .then(d => ({ url: d.revisionPdfUrl, reanchored: !!d.reanchored }))
}

/**
 * The corrected drawing CLOSES this round and OPENS the next (round = one PDF): uploads the
 * file, then the server spawns the child round (carrying every non-verified/dismissed item as-is,
 * with lineage) and supersedes the current one. Returns { version } — the NEW round. Rejects
 * (409) while any item is still open (not addressed/dismissed).
 */
export async function startNextRound(versionId, file) {
  const url = await uploadQcPdf(versionId, file, 'nextbase')
  const d = await call('POST', { action: 'startNextRound', versionId, pdfUrl: url })
  return { version: d.version }
}

/**
 * Delete a Sheet Revision's ENTIRE QC chain — every round, finding, comment, AI run, and the
 * copied PDFs in Storage. QC is revision-scoped, so removing a sheet from a milestone takes its
 * QC work with it. Irreversible; callers gate it behind a typed confirmation
 * (RemoveSheetModal). Returns { scrubbed } — the number of rounds deleted.
 */
export function scrubRevisionQc(sheetRevisionId, drawingSheetId) {
  return call('POST', { action: 'scrubRevision', sheetRevisionId, drawingSheetId })
}

/**
 * Run the AI back-check comparing prior (marked-up) vs revised. Single mode — sends the
 * prior PDF + revised PDF + findings (+ flattened markup when provided).
 * Returns { results: {findingId: {resolved, confidence, note}} }.
 */
export async function runBackCheck({ versionId, sheetId, revisionId, priorPdfUrl, revisedPdfUrl, flattenedPdfUrl, findings, phasePrefix }) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/ai-qc', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
    body: JSON.stringify({ action: 'backcheck', versionId, sheetId, revisionId, priorPdfUrl, revisedPdfUrl, flattenedPdfUrl, findings, phasePrefix }),
  })
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || 'Back-check failed: ' + res.status) }
  return res.json()
}

/**
 * Official "canonical" approved PDF for a drawing sheet (Supabase) — the end-of-QC-rounds approved
 * drawing (or an admin's manual replacement of it) for ONE SPECIFIC revision. Always pass
 * `revisionId` when the caller knows which revision it means (the caller has almost always already
 * resolved "the current revision" via Posted Date, e.g. sheetRevCache[0] or a milestone's own linked
 * revisions) — this scopes the lookup to that revision's own approval, never a different revision of
 * the same physical sheet. Omitting `revisionId` falls back to sheet-wide "most recently approved,
 * any revision" — kept only for safety on a caller with no revision context; this was the exact
 * mechanism behind the 2026-07-22 stale-export incident (an old revision's late QC approval
 * out-ranked the real latest revision by wall-clock time alone) and should not be relied on for new
 * code. Returns { url, versionId, approvedAt, isQcApproved }.
 */
export function getOfficialPdf(drawingSheetId, revisionId) {
  const qs = 'officialPdf=' + encodeURIComponent(drawingSheetId) + (revisionId ? '&revisionId=' + encodeURIComponent(revisionId) : '')
  return call('GET', null, qs)
}

/** Per-REVISION QC summary for a set of Sheet Revision IDs → { [revisionId]: {hasVersion, approved, total, verified, addressed, open, dismissed, done} }. */
export function getRevisionQcSummaries(revisionIds) {
  if (!revisionIds || !revisionIds.length) return Promise.resolve({})
  return call('GET', null, 'summaryRev=' + encodeURIComponent(revisionIds.join(','))).then(d => d.summaries || {})
}

/**
 * Canonical QC status for a sheet, derived from a revision QC summary `sm`
 * (getRevisionQcSummaries, or the QC tool's live summary). SINGLE SOURCE OF TRUTH shared by the QC
 * tool's sheet list (QcMarkupModal.renderSheetMetric) and the outline drawing-sheet badge
 * (DrawingSheetList.qcStatusBadge) so the two can never drift. Returns
 * { label, count, color, countColor } or null — `count` is '' when there's no progress fraction.
 */
export function qcSheetStatus(sm) {
  if (!sm) return null
  if (sm.isOverride) return { label: '✓ Approved (Override)', count: '', color: '#1D9E75', countColor: '#1D9E75' }
  if (sm.approved || sm.status === 'approved') return { label: '✓ Approved', count: '', color: '#1D9E75', countColor: '#1D9E75' }
  if (!sm.hasVersion) return { label: 'no QC', count: '', color: '#9aa0a6', countColor: '#9aa0a6' }
  if (sm.status === 'review_complete') {
    if (sm.hasRevision) return { label: 'Revised — back-check', count: '', color: '#1a6fa8', countColor: '#1a6fa8' }  // LEGACY shape (mid-round revised PDF)
    const handled = (sm.total || 0) - (sm.open || 0)                                                                  // addressed/dismissed/verified
    if (handled <= 0) return { label: 'Ready for revisions', count: '', color: '#b8540a', countColor: '#b8540a' }     // not started yet
    return { label: 'Revising', count: handled + '/' + sm.total, color: '#d97706', countColor: '#d97706' }            // drafter working the items
  }
  // in_review — being checked. Carried addressed items can only exist via a round boundary
  // (upload of the corrected drawing), so their presence = this round opened as a BACK-CHECK.
  if (!sm.total) return { label: 'Under QC Review', count: '', color: '#7a9aaa', countColor: '#7a9aaa' }
  if ((sm.addressed || 0) > 0) {
    return { label: 'Back-check', count: sm.done + '/' + sm.total, color: '#1a6fa8', countColor: sm.done / sm.total >= 1 ? '#1D9E75' : '#1a6fa8' }
  }
  // Count color matches the label color (gray) until fully done (green) — no orange midpoint.
  // Orange elsewhere in this file means "with the drafter, actively being worked"; a sheet still
  // under QC review hasn't reached that stage, so an orange count here read as a mismatched signal.
  const frac = sm.done / sm.total
  const countColor = frac >= 1 ? '#1D9E75' : '#7a9aaa'
  // "done" only counts verified/dismissed items, so a sheet the AI has fully reviewed (findings
  // exist, none dismissed yet) reads identically to a sheet nobody has touched at all — both show
  // "Under QC Review 0/N". Suffix the label with what's actually happened (AI first pass run
  // and/or a human note added) so that distinction is visible without opening the tool.
  const aiRan = (sm.aiTotal || 0) > 0, humanTouched = (sm.noteTotal || 0) > 0
  const suffix = aiRan && humanTouched ? ' · AI+Manual' : aiRan ? ' · AI' : humanTouched ? ' · Manual' : ''
  return { label: 'Under QC Review' + suffix, count: sm.done + '/' + sm.total, color: '#7a9aaa', countColor }
}

/** The QC round chain for one Sheet Revision (History tab), newest first. */
export function getRevisionVersions(sheetRevisionId) {
  return call('GET', null, 'historyRev=' + encodeURIComponent(sheetRevisionId)).then(d => d.versions || [])
}

/* QC by agreement (2026-10-25): sheets never run through QC are called approved once the Design Director
   and the Technical Director both agree, with a stated reason (api/qc-versions.js). */
export function requestQcAgreement({ milestoneId, milestoneName, projectName, sheets, reason, memberId, memberName }) {
  return call('POST', { action: 'requestAgreement', milestoneId, milestoneName, projectName, sheets, reason, memberId, memberName }).then(d => d.agreement)
}
export function answerQcAgreement(id, decision, { memberId, memberName, note } = {}) {
  return call('POST', { action: 'agree', id, decision, memberId, memberName, note }).then(d => d.agreement)
}
export function getQcAgreements(milestoneId) {
  return call('POST', { action: 'agreements', milestoneId }).then(d => d.agreements || [])
}
